// A member's own sign-in methods (ADR 0032): password and passkeys. Open to every signed-in
// member, reps included, so the desktop can send people here to add a passkey in the browser.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { KiteMark, Button as VdButton } from "@vd/ui";
import { KeyRound, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { meQuery } from "../api/admin";
import { ApiError, errorMessage } from "../api/client";
import { me, mySignInQuery, type PasskeyView, signinKeys } from "../api/signin";
import { cancelled, createPasskey, deviceName, passkeysSupported } from "../auth/passkey";
import { NewPasswordFields, passwordsMatch } from "../components/signin/NewPasswordFields";
import { toast } from "../components/toast";
import { Button, Dialog, Field, Input, Loading } from "../components/ui";
import { relTime } from "../lib/format";

export const Route = createFileRoute("/account")({
  staticData: { crumb: { label: "Account", parents: [] } },
  beforeLoad: async ({ context, location }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({ to: "/signin", search: { redirect: location.href } });
      }
      throw e;
    }
  },
  component: Account,
});

const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

function passkeyLine(p: PasskeyView): string {
  const added = `Added ${day.format(new Date(p.created_at))}`;
  return p.last_used_at ? `${added} · Last used ${relTime(p.last_used_at)}` : added;
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-12 items-center gap-3 border-hairline border-b px-4 py-2 last:border-b-0">
      {children}
    </div>
  );
}

/** True (and on the way to sign in again) when the server asks for a fresh sign-in first. */
function reauth(e: unknown): boolean {
  if (!(e instanceof ApiError) || e.code !== "reauth_required") return false;
  const q = new URLSearchParams({ redirect: "/account", error: "reauth_required" });
  window.location.assign(`/signin?${q.toString()}`);
  return true;
}

function ChangePassword({ open, onClose, first }: { open: boolean; onClose: () => void; first: boolean }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => me.changePassword(first ? undefined : current, next, confirm),
    onSuccess: () => {
      toast(first ? "Password set" : "Password changed");
      setCurrent("");
      setNext("");
      setConfirm("");
      setSubmitted(false);
      onClose();
    },
    onError: (e) => {
      if (!reauth(e)) setProblem(errorMessage(e));
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setProblem(null);
    if (!passwordsMatch(next, confirm)) return;
    m.mutate();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={first ? "Set a password" : "Change password"}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="change-password"
            busy={m.isPending}
            disabled={(!first && !current) || !next || !confirm}
          >
            Save
          </Button>
        </>
      }
    >
      <form id="change-password" onSubmit={submit} className="flex flex-col gap-3">
        {first ? null : (
          <Field label="Current password">
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
        )}
        <NewPasswordFields
          password={next}
          confirm={confirm}
          onPassword={setNext}
          onConfirm={setConfirm}
          submitted={submitted}
          problem={problem}
          idPrefix="change"
        />
      </form>
    </Dialog>
  );
}

function Account() {
  const qc = useQueryClient();
  const q = useQuery(mySignInQuery);
  const [changing, setChanging] = useState(false);
  const [naming, setNaming] = useState<{ credential: unknown; name: string } | null>(null);
  const [removing, setRemoving] = useState<PasskeyView | null>(null);
  const [adding, setAdding] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: signinKeys.mine });

  const save = useMutation({
    mutationFn: (v: { credential: unknown; name: string }) => me.addPasskey(v.name, v.credential),
    onSuccess: () => {
      setNaming(null);
      toast("Passkey added");
      void refresh();
    },
    onError: (e) => {
      if (!reauth(e)) toast(errorMessage(e), "danger");
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => me.removePasskey(id),
    onSuccess: () => {
      setRemoving(null);
      toast("Passkey removed");
      void refresh();
    },
    onError: (e) => toast(errorMessage(e), "danger"),
  });

  const add = async () => {
    setAdding(true);
    try {
      const { public_key } = await me.passkeyOptions();
      const credential = await createPasskey(public_key);
      setNaming({ credential, name: deviceName() });
    } catch (e) {
      if (!cancelled(e) && !reauth(e)) toast(errorMessage(e), "danger");
    } finally {
      setAdding(false);
    }
  };

  return (
    <main className="min-h-full bg-canvas px-4 py-10">
      <div className="mx-auto flex w-full max-w-[680px] flex-col gap-6">
        <div className="flex items-center gap-2.5">
          <KiteMark size={24} />
          <span className="font-medium text-title tracking-tight">Kite</span>
        </div>
        <h1 className="font-display font-medium text-heading">Account</h1>
        <section aria-labelledby="signin-group" className="flex flex-col gap-2">
          <h2 id="signin-group" className="font-medium text-caption text-ink-3">
            Sign-in
          </h2>
          {q.isPending ? (
            <Loading />
          ) : q.isError ? (
            <p className="text-caption text-danger-ink">{errorMessage(q.error)}</p>
          ) : (
            <div className="rounded-xl bg-surface-1 shadow-material">
              <Row>
                <div className="min-w-0 flex-1">
                  <p className="text-ui">Password</p>
                  <p className="text-caption text-ink-3">{q.data.password_set ? "Set" : "Not set"}</p>
                </div>
                {q.data.password_set ? (
                  <Button onClick={() => setChanging(true)}>Change</Button>
                ) : (
                  <Button onClick={() => setChanging(true)}>Set a password</Button>
                )}
              </Row>
              {q.data.passkeys.map((p) => (
                <Row key={p.id}>
                  <KeyRound className="size-4 shrink-0 text-ink-3" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-ui">{p.name}</p>
                    <p className="text-caption text-ink-3">{passkeyLine(p)}</p>
                  </div>
                  <VdButton
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove ${p.name}`}
                    title={`Remove ${p.name}`}
                    onClick={() => setRemoving(p)}
                  >
                    <Trash2 />
                  </VdButton>
                </Row>
              ))}
              <Row>
                <div className="min-w-0 flex-1">
                  <p className="text-ui">Passkeys</p>
                  <p className="text-caption text-ink-3">
                    {q.data.passkeys.length === 0 ? "None" : `${q.data.passkeys.length}`}
                  </p>
                </div>
                <VdButton
                  leadingIcon={<KeyRound />}
                  loading={adding}
                  disabled={!passkeysSupported() || adding}
                  onClick={() => void add()}
                >
                  Add a passkey
                </VdButton>
              </Row>
            </div>
          )}
        </section>
      </div>
      <ChangePassword open={changing} onClose={() => setChanging(false)} first={!q.data?.password_set} />
      <Dialog
        open={!!naming}
        onClose={() => setNaming(null)}
        title="Name this passkey"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setNaming(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={save.isPending}
              disabled={!naming?.name.trim()}
              onClick={() => naming && save.mutate(naming)}
            >
              Save
            </Button>
          </>
        }
      >
        <Field label="Name">
          <Input
            value={naming?.name ?? ""}
            maxLength={80}
            onChange={(e) => setNaming((n) => (n ? { ...n, name: e.target.value } : n))}
          />
        </Field>
      </Dialog>
      <Dialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name ?? "passkey"}?`}
        size="sm"
        tone="danger"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              busy={remove.isPending}
              onClick={() => removing && remove.mutate(removing.id)}
            >
              Remove
            </Button>
          </>
        }
      />
    </main>
  );
}
